"use client";

import { FeaturedIn } from "@/components/FeaturedIn";
import {
  Box,
  Container,
  Title,
  Text,
  Button,
  SimpleGrid,
  Stack,
  Paper,
  Modal,
  Flex,
  rem,
  Divider,
  Group,
  Badge,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconCloudUpload,
  IconX,
  IconCamera,
  IconDownload,
  IconShirt,
  IconBriefcase,
  IconSparkles,
  IconMoon,
  IconLeaf,
  IconSun,
  IconCheck,
  IconArrowRight,
} from "@tabler/icons-react";
import React, { useRef, useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { PRICING } from "@/constants/pricing";

// ============================================
// STYLE PACK DEFINITIONS - Aesthetic-Based Identity Exploration
// ============================================

// Row 1: Everyday (Casual, Smart, Business, Corporate)
// Row 2: Lifestyle (Lounge, Street, Athleisure, Summer)
// Row 3: Occasion (Winter, Date, Formal, Minimal)
export const STYLE_PACKS = [
  // Row 1: Everyday
  {
    id: "casual-everyday",
    name: "Casual",
    description: "Jeans, knits, and sneakers.",
    icon: IconLeaf,
    color: "#64748B",
    vibeBoard: "/vibeboards/casual-everyday.png",
  },
  {
    id: "smart-casual",
    name: "Smart",
    description: "Blazer + jeans energy.",
    icon: IconBriefcase,
    color: "#78716C",
    vibeBoard: "/vibeboards/smart-casual.png",
  },
  {
    id: "business-casual",
    name: "Business",
    description: "Polished, no denim.",
    icon: IconBriefcase,
    color: "#3B82F6",
    vibeBoard: "/vibeboards/business-casual.png",
  },
  {
    id: "corporate-formal",
    name: "Corporate",
    description: "Suits and structured pieces.",
    icon: IconBriefcase,
    color: "#1E293B",
    vibeBoard: "/vibeboards/corporate-formal.png",
  },
  // Row 2: Lifestyle
  {
    id: "weekend-lounge",
    name: "Lounge",
    description: "Cozy errands mode.",
    icon: IconSun,
    color: "#10B981",
    vibeBoard: "/vibeboards/weekend-lounge.png",
  },
  {
    id: "street-trend",
    name: "Street",
    description: "Cargo, oversized, layered.",
    icon: IconShirt,
    color: "#8B5CF6",
    vibeBoard: "/vibeboards/street-trend.png",
  },
  {
    id: "athleisure",
    name: "Athleisure",
    description: "Performance-ready fits.",
    icon: IconSun,
    color: "#EF4444",
    vibeBoard: "/vibeboards/athleisure.png",
  },
  {
    id: "summer-casual",
    name: "Summer",
    description: "Shorts, linen, sandals.",
    icon: IconSun,
    color: "#F59E0B",
    vibeBoard: "/vibeboards/summer-casual.png",
  },
  // Row 3: Occasion
  {
    id: "winter-layers",
    name: "Winter",
    description: "Coats, boots, warmth.",
    icon: IconLeaf,
    color: "#6366F1",
    vibeBoard: "/vibeboards/winter-layers.png",
  },
  {
    id: "date-night",
    name: "Date",
    description: "Sleek evening looks.",
    icon: IconMoon,
    color: "#EC4899",
    vibeBoard: "/vibeboards/date-night.png",
  },
  {
    id: "formal-event",
    name: "Formal",
    description: "Black tie ready.",
    icon: IconSparkles,
    color: "#7C3AED",
    vibeBoard: "/vibeboards/formal-event.png",
  },
  {
    id: "elevated-minimal",
    name: "Minimal",
    description: "Monochrome, clean lines.",
    icon: IconSparkles,
    color: "#475569",
    vibeBoard: "/vibeboards/elevated-minimal.png",
  },
];

// ============================================
// UTILITIES
// ============================================

function getBaseOutfitCount(): number {
  const baseCount = 12847;
  const baseDate = new Date("2025-02-01").getTime();
  const now = Date.now();
  const daysSinceBase = Math.floor((now - baseDate) / (1000 * 60 * 60 * 24));
  return baseCount + daysSinceBase * 45;
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function Content() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isMobile = useMediaQuery("(max-width: 48em)");

  // Landing page state
  const baseCount = getBaseOutfitCount();
  const [displayCount, setDisplayCount] = useState<number | null>(null);

  // Camera modal
  const [cameraOpen, { open: openCamera, close: closeCamera }] = useDisclosure(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Screenshot zoom modal
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  // Check if coming from color analysis (for bundle pricing)
  const fromColor = searchParams.get("from") === "color";
  const presetSeason = searchParams.get("season");
  const presetKibbe = searchParams.get("kibbe");

  // ============================================
  // COUNTER ANIMATION
  // ============================================

  useEffect(() => {
    const target = baseCount;
    const duration = 2000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(target * easeOut);
      setDisplayCount(current);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [baseCount]);

  // Store color bundle info in sessionStorage when coming from color analysis
  useEffect(() => {
    if (fromColor) {
      sessionStorage.setItem("outfitFromColor", "true");
      if (presetSeason) {
        sessionStorage.setItem("outfitPresetSeason", presetSeason);
      }
    }
    // Store Kibbe type when coming from Kibbe pages
    if (presetKibbe) {
      sessionStorage.setItem("outfitPresetKibbe", presetKibbe);
    }
  }, [fromColor, presetSeason, presetKibbe]);

  // ============================================
  // IMAGE HANDLING
  // ============================================

  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.round(width * scale);
        height = Math.round(height * scale);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        URL.revokeObjectURL(img.src);
        resolve(dataUrl);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoDrop = async (files: File[]) => {
    if (files.length > 0) {
      const dataUrl = await resizeImage(files[0], 1200, 1200);
      sessionStorage.setItem("outfitGeneratorImage", dataUrl);
      router.push("/outfit-wizard");
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = async () => {
    if (videoRef.current && captureCanvasRef.current) {
      const video = videoRef.current;
      const canvas = captureCanvasRef.current;

      let width = video.videoWidth;
      let height = video.videoHeight;
      const scale = Math.min(1200 / width, 1200 / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        sessionStorage.setItem("outfitGeneratorImage", dataUrl);
        closeCamera();
        stopCamera();
        router.push("/outfit-wizard");
      }
    }
  };

  const handleOpenCamera = () => {
    openCamera();
    setTimeout(() => startCamera(), 100);
  };

  const handleCloseCamera = () => {
    stopCamera();
    closeCamera();
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box mt={{ base: "1rem", md: "3.5rem" }} pos="relative">
      {/* Hero Section */}
      <Box pt={{ base: 0, md: "md" }} pb={{ base: "sm", md: "xl" }}>
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "md", md: "xl" }}>
            {/* Left: Copy */}
            <Stack gap="sm" justify="center">
              <Title
                order={1}
                fz={{ base: "1.5rem", md: "2.5rem" }}
                fw={700}
                style={{ lineHeight: 1.15, color: "#1E293B" }}
                ta={{ base: "center", md: "left" }}
              >
                AI Outfit Generator
              </Title>
              <Text fz={{ base: "sm", md: "lg" }} ta={{ base: "center", md: "left" }} style={{ color: "#64748B" }}>
                See yourself in different outfits instantly. Upload a photo, tell us your gender (and optionally your color season + Kibbe body type), and get ~30 AI-generated looks styled for you.
              </Text>
              <Text fz="xs" ta={{ base: "center", md: "left" }} style={{ color: "#94A3B8" }}>
                No signup · Results in minutes · Photos stay private
              </Text>
            </Stack>

            {/* Right: Upload Box */}
            <Paper
              radius="lg"
              p={{ base: "md", md: "xl" }}
              style={{
                background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              }}
            >
              <Stack gap="sm">
                <Box ta="center">
                  <Text fw={600} fz={{ base: "md", md: "lg" }} style={{ color: "#1E293B" }}>
                    Upload Photo → Choose Styles → See Outfits
                  </Text>
                </Box>

                <Dropzone
                  onDrop={handlePhotoDrop}
                  maxSize={10 * 1024 ** 2}
                  accept={IMAGE_MIME_TYPE}
                  multiple={false}
                  h={130}
                  radius="lg"
                  styles={{
                    root: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "white",
                      border: "2px dashed #CBD5E1",
                      "&:hover": {
                        borderColor: "#8B5CF6",
                        backgroundColor: "#F8FAFC",
                      },
                    },
                  }}
                >
                  <Flex direction="column" align="center" justify="center" style={{ pointerEvents: "none" }}>
                    <Dropzone.Accept>
                      <IconDownload style={{ width: rem(36), height: rem(36), color: "#22C55E" }} stroke={1.5} />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX style={{ width: rem(36), height: rem(36), color: "#EF4444" }} stroke={1.5} />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <IconCloudUpload style={{ width: rem(36), height: rem(36), color: "#94A3B8" }} stroke={1.5} />
                    </Dropzone.Idle>
                    <Text size="sm" ta="center" mt="xs" fw={500} style={{ color: "#475569" }}>
                      Drag photo here or click to select
                    </Text>
                    <Text size="xs" ta="center" mt={4} style={{ color: "#94A3B8" }}>
                      Full body or half body works best
                    </Text>
                  </Flex>
                </Dropzone>

                {!isMobile && (
                  <>
                    <Divider label="or" labelPosition="center" color="gray.3" my={0} />

                    <Button
                      variant="light"
                      color="violet"
                      size="md"
                      radius="md"
                      leftSection={<IconCamera size={18} />}
                      onClick={handleOpenCamera}
                      fullWidth
                    >
                      Use Your Camera
                    </Button>
                  </>
                )}
              </Stack>
            </Paper>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Social Proof */}
      <Box py={{ base: "sm", md: "lg" }} bg="#F8FAFC">
        <Container size="md" ta="center">
          <Title order={2} fz={{ base: "1rem", md: "1.75rem" }} mb={4} style={{ color: "#1E293B" }}>
            <Text span fz={{ base: "1.25rem", md: "2rem" }} fw={700} style={{ color: "#8B5CF6", visibility: displayCount === null ? "hidden" : "visible" }}>
              {(displayCount ?? 0).toLocaleString()}
            </Text>{" "}
            outfits generated
          </Title>
          <Box mt={{ base: "xs", md: "sm" }} mb={{ base: "sm", md: "lg" }}>
            <FeaturedIn />
          </Box>
        </Container>
      </Box>

      {/* Real AI-Generated Results - THE PROOF SECTION */}
      <Box py={{ base: "md", md: "xl" }}>
        <Container size="lg">
          <Title order={2} ta="center" mb="xs" fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            Real AI-Generated Results
          </Title>
          <Text ta="center" mb={{ base: "md", md: "lg" }} fz={{ base: "sm", md: "md" }} style={{ color: "#64748B" }}>
            Same person, different outfits. Your face and body, new looks.
          </Text>

          <Stack gap="md">
            {[
              { label: "Formal", before: "/outfitGenerator/sample3.png", afters: ["/outfitGenerator/formal_1.png", "/outfitGenerator/formal_2.png", "/outfitGenerator/formal_3.png", "/outfitGenerator/formal_4.png"] },
              { label: "Casual", before: "/outfitGenerator/sample2.webp", afters: ["/outfitGenerator/casual_1.png", "/outfitGenerator/casual_2.png", "/outfitGenerator/casual_3.png", "/outfitGenerator/casual_4.png"] },
              { label: "Business", before: "/outfitGenerator/sample1.webp", afters: ["/outfitGenerator/business_1.png", "/outfitGenerator/business_2.png", "/outfitGenerator/business_3.png", "/outfitGenerator/business_4.png"] },
              { label: "Date Night", before: "/outfitGenerator/sample4.webp", afters: ["/outfitGenerator/date_1.png", "/outfitGenerator/date_2.png", "/outfitGenerator/date_3.png", "/outfitGenerator/date_4.png"] },
            ].map((item, i) => (
              <Paper key={i} p={{ base: "sm", md: "md" }} radius="lg" withBorder>
                <Text fw={700} fz={{ base: "sm", md: "md" }} mb="sm" style={{ color: "#1E293B" }}>
                  {item.label}
                </Text>
                <Flex gap="xs" align="stretch">
                  {/* Upload */}
                  <Box style={{ flex: 1 }}>
                    <Text fz={10} fw={600} ta="center" mb={4} tt="uppercase" style={{ color: "#94A3B8", letterSpacing: 1 }}>
                      You Upload
                    </Text>
                    <Box
                      style={{
                        aspectRatio: "3/4",
                        borderRadius: 8,
                        overflow: "hidden",
                        border: "2px solid #E2E8F0",
                        background: "#F1F5F9",
                      }}
                    >
                      <img
                        src={item.before}
                        alt="Your photo"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        loading="lazy"
                      />
                    </Box>
                  </Box>

                  {/* Arrow */}
                  <Flex align="center" justify="center" style={{ flex: "0 0 auto" }}>
                    <IconArrowRight size={20} color="#8B5CF6" />
                  </Flex>

                  {/* Generated outfits */}
                  {item.afters.map((src, j) => (
                    <Box key={j} style={{ flex: 1 }}>
                      {j === 0 && (
                        <Text fz={10} fw={600} ta="center" mb={4} tt="uppercase" style={{ color: "#10B981", letterSpacing: 1 }}>
                          You Get
                        </Text>
                      )}
                      {j !== 0 && <Box h={18} />}
                      <Box
                        onClick={() => setZoomImage(src)}
                        style={{
                          aspectRatio: "3/4",
                          borderRadius: 8,
                          overflow: "hidden",
                          border: "2px solid #10B981",
                          cursor: "pointer",
                          background: "#F0FDF4",
                        }}
                      >
                        <img
                          src={src}
                          alt={`${item.label} outfit ${j + 1}`}
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                          loading="lazy"
                        />
                      </Box>
                    </Box>
                  ))}
                </Flex>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* How It Works */}
      <Box py={{ base: "md", md: "xl" }} bg="#F8FAFC">
        <Container size="md">
          <Title order={2} ta="center" mb={{ base: "md", md: "lg" }} fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            How It Works
          </Title>
          <SimpleGrid cols={3} spacing={{ base: "xs", md: "md" }}>
            {[
              { step: "1", title: "Upload", desc: "Add a photo of yourself" },
              { step: "2", title: "Choose", desc: "Pick 3 style categories" },
              { step: "3", title: "See", desc: "Get ~30 outfit looks" },
            ].map((item) => (
              <Stack key={item.step} align="center" gap={4}>
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "#8B5CF6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text fw={700} fz="sm" c="white">{item.step}</Text>
                </Box>
                <Text fw={700} fz={{ base: "sm", md: "md" }} style={{ color: "#1E293B" }}>{item.title}</Text>
                <Text fz={{ base: "xs", md: "sm" }} ta="center" style={{ color: "#64748B" }}>{item.desc}</Text>
              </Stack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Style Variety Preview - Lightweight */}
      <Box py={{ base: "md", md: "xl" }}>
        <Container size="md">
          <Title order={2} ta="center" mb="xs" fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            12 Style Categories
          </Title>
          <Text ta="center" mb={{ base: "md", md: "lg" }} fz={{ base: "sm", md: "md" }} style={{ color: "#64748B" }}>
            From casual to formal, athleisure to date night
          </Text>
          <Flex gap={{ base: 6, md: "xs" }} wrap="wrap" justify="center">
            {STYLE_PACKS.map((pack) => (
              <Badge
                key={pack.id}
                size="lg"
                radius="md"
                variant="light"
                color="gray"
                style={{ textTransform: "none", fontWeight: 500 }}
              >
                {pack.name}
              </Badge>
            ))}
          </Flex>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={{ base: "sm", md: "lg" }} bg="#F8FAFC">
        <Container size="xl">
          <Title order={2} ta="center" mb={{ base: "sm", md: "lg" }} style={{ color: "#1E293B" }}>
            Frequently Asked Questions
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {[
              {
                question: "What is an AI outfit generator?",
                answer: "An AI outfit generator uses artificial intelligence to show you wearing different outfits. Upload a photo of yourself and see realistic previews of how you'd look in various styles.",
              },
              {
                question: "How does it work?",
                answer: "Upload a clear photo of yourself, choose up to 3 style packs (like casual, work, or streetwear), and our AI generates approximately 30 realistic outfit previews showing you in those styles.",
              },
              {
                question: "What kind of photo should I use?",
                answer: "A clear, well-lit photo works best. Full body or half body (waist up) photos give the best results. Avoid heavy filters or unusual poses.",
              },
              {
                question: "How long does it take?",
                answer: "Results are typically ready in under 60 seconds. You'll see your outfit previews appear in a gallery as they're generated.",
              },
              {
                question: "Can I download the images?",
                answer: "Yes! You can browse all your outfit previews in a gallery view and download them all as a ZIP file to keep forever.",
              },
              {
                question: "Why do you ask for gender?",
                answer: "Gender drives both the style of outfits we generate and how the AI renders the photos. We ask explicitly so you don't end up with menswear when you wanted womenswear (or vice versa) — instead of guessing from your photo. Female, male, and non-binary / androgynous are all supported.",
              },
              {
                question: "What's the color season option?",
                answer: "If you know your color season (like Spring, Summer, Autumn, or Winter), you can add it to get outfits in colors that flatter you best. You can also add a color analysis if you don't know your season.",
              },
              {
                question: "Can I personalize outfits for my body type?",
                answer: "Yes! If you know your Kibbe body type (like Flamboyant Natural, Soft Dramatic, etc.), select it at checkout. We then apply that type's specific styling rules — line, silhouette, scale, fabric, accessories — so a Flamboyant Natural's 'smart casual' looks different from a Classic's, the way it should.",
              },
              {
                question: "Is this a virtual try-on or fitting room?",
                answer: "Yes! Think of it as a virtual fitting room that shows you wearing actual outfits. Unlike apps that just overlay clothes on a mannequin, our AI generates realistic images of YOU in each outfit, accounting for your body shape and proportions.",
              },
              {
                question: "Can I use this for outfit inspiration?",
                answer: "Absolutely. Many people use it to discover new styles they wouldn't normally try. See yourself in streetwear, minimalist looks, or elegant evening wear—styles you might never pick off a rack but could actually love on you.",
              },
              {
                question: "How accurate are the AI-generated outfits?",
                answer: "We instruct the AI to keep your exact face, body proportions, height, and frame — no slimming, no lengthening, no editorial-model 'idealizing.' That said, AI image generation is imperfect: occasionally a photo will drift in proportions or a detail won't come out clean. If a result feels off to you, send us a fresh photo and we'll re-generate your outfits for free.",
              },
            ].map((faq, index) => (
              <Paper key={index} p="lg" radius="lg" bg="white" style={{ border: "1px solid #E2E8F0" }}>
                <Title order={4} mb="sm" fz="md" style={{ color: "#1E293B" }}>
                  {faq.question}
                </Title>
                <Text fz="sm" style={{ color: "#64748B" }}>
                  {faq.answer}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box py={{ base: "md", md: "xl" }} bg="#F8FAFC">
        <Container size="sm" ta="center">
          <Title order={2} mb="sm" fz={{ base: "1.25rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            Ready to See Your New Looks?
          </Title>
          <Text mb="md" style={{ color: "#64748B" }}>
            Upload a photo and choose your styles. Results in minutes.
          </Text>
          <Button
            size="lg"
            color="violet"
            radius="md"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Get Started - ${PRICING.OUTFIT_GENERATOR.BASE_PRICE}
          </Button>
        </Container>
      </Box>

      {/* Camera Modal */}
      <Modal opened={cameraOpen} onClose={handleCloseCamera} title="Take a Photo" centered size="md">
        <Stack>
          <Box h={350} style={{ overflow: "hidden", borderRadius: 12 }}>
            <video ref={videoRef} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
          <Button onClick={takePhoto} leftSection={<IconCamera size={18} />} color="violet" size="lg" radius="md">
            Capture Photo
          </Button>
          <canvas ref={captureCanvasRef} style={{ display: "none" }} />
        </Stack>
      </Modal>

      {/* Screenshot Zoom Modal */}
      <Modal
        opened={!!zoomImage}
        onClose={() => setZoomImage(null)}
        size="xl"
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: { padding: 0 },
          content: { background: "transparent", boxShadow: "none" },
        }}
        overlayProps={{ backgroundOpacity: 0.7 }}
      >
        <Box
          onClick={() => setZoomImage(null)}
          style={{
            cursor: "pointer",
            maxHeight: "90vh",
            overflow: "auto",
            borderRadius: 16,
            background: "white",
          }}
        >
          {zoomImage && (
            <img
              src={zoomImage}
              alt="Full preview"
              style={{ width: "100%", height: "auto", display: "block", borderRadius: 16 }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}
